"use client";

import { jwtDecode } from "jwt-decode";
import { createContext, useContext } from "react";
import { isWellFormedJwt } from "@/lib/jwt-validation";

type AuthContextType = {
  token?: string;
};

type JwtPayload = {
  ID: string;
  exp: number;
  iat: number;
  kind: string;
};

type UseAuthReturnType = {
  /** ログインしているか */
  isLoggedIn: boolean;
  /** トークン */
  token: string | undefined;
  /** ユーザーID（email） */
  ID: string | undefined;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({
  children,
  token,
}: {
  children: React.ReactNode;
  token: string | undefined;
}) => {
  return (
    <AuthContext.Provider value={{ token }}>{children}</AuthContext.Provider>
  );
};

/**
 * 認証状態を取得する。
 *
 * トークンが JWT として不正な形式の場合は未認証として扱います。
 */
export const useAuth = (): UseAuthReturnType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  const token = context.token;

  // JWT 形式でないトークンは無効とみなす
  if (!token || !isWellFormedJwt(token)) {
    return { isLoggedIn: false, token: undefined, ID: undefined };
  }

  try {
    const decoded = jwtDecode<JwtPayload>(token);
    return { isLoggedIn: true, token, ID: decoded.ID };
  } catch {
    // jwt-decode の InvalidTokenError（base64 不正等）は Bugsnag 通知不要
    return { isLoggedIn: false, token: undefined, ID: undefined };
  }
};
